import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SmsModule } from '@galaxy/sms';
import { TranslateModule } from '@ngx-translate/core';
import {
  Configuration,
  ConversationApiService,
  GlobalParticipantType,
  InboxApiService,
  Widget,
} from '@vendasta/conversation';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_FACEBOOK_MESSENGER_AVAILABLE_TOKEN,
  CONVERSATION_GOOGLE_BUSINESS_MESSAGES_AVAILABLE_TOKEN,
  CONVERSATION_INSTAGRAM_MESSAGES_AVAILABLE_TOKEN,
  InboxService,
  PARTNER_ID_TOKEN,
} from '../../../core/src/index';
import { InboxPreviewComponent } from '../inbox-preview/inbox-preview.component';
import { EmailSettingsCardComponent } from './email-configuration/email-settings-card.component';
import { FacebookMessengerComponent } from './facebook-messenger/facebook-messenger.component';
import { GoogleBusinessCommunicationCardComponent } from './google-business-communication-card/google-business-communication-card.component';
import { InstagramMessagesComponent } from './instagram-messages/instagram-messages.component';
import { WebchatConfigCardComponent } from './webchat-configuration/webchat-config-card/webchat-config-card.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    GalaxyPageModule,
    GalaxyFormFieldModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatSlideToggleModule,
    MatIconModule,
    InboxPreviewComponent,
    GalaxyEmptyStateModule,
    SmsModule,
    GoogleBusinessCommunicationCardComponent,
    FacebookMessengerComponent,
    InstagramMessagesComponent,
    WebchatConfigCardComponent,
    EmailSettingsCardComponent,
  ],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent implements OnInit {
  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  protected readonly loading$ = this.loading$$.asObservable();
  protected readonly showAvailabilityMessageSettings$ = this.inboxService.showAvailabilityMessageSettings$;
  protected readonly canAccessWebChatSettingsCard$ = this.inboxService.canAccessWebChatSettings$;
  protected readonly canAccessMessageTemplateSettingsCard$ = this.inboxService.canAccessMessageTemplateSettingsCard$;
  protected readonly canAccessEmailChannel$ = this.inboxService.canAccessEmailChannel$;
  // Only show save button if there is at least one setting that requires saving.
  protected readonly showSaveButton$ = this.showAvailabilityMessageSettings$;

  protected readonly form = new FormGroup({
    showAvailabilityMessage: new FormControl(false),
    availabilityMessage: new FormControl(''),
  });

  protected readonly isPartnerCenter = this.inboxService.isPartnerCenter;
  protected readonly isBusinessApp = this.inboxService.isBusinessApp;
  protected readonly displayA2pCard$ = this.inboxService.displayA2pCard$();
  protected readonly displayGoogleBusinessCommunicationCard$: Observable<boolean>;
  protected readonly displayEmptyState$: Observable<boolean>;

  private accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  protected readonly webchats$: Observable<Widget[]> = this.partnerId$.pipe(
    switchMap((partnerId) => {
      const namespaceID = this.isPartnerCenter ? partnerId : this.accountGroupId();
      return this.conversationApiService.listWidgets({
        namespace: namespaceID,
      });
    }),
    map((listWidgets) => listWidgets?.widgets),
    shareReplay({ bufferSize: 1, refCount: true }),
    catchError(async (err) => {
      return err;
    }),
  );
  protected readonly webchatStatus$: Observable<boolean> = this.webchats$.pipe(map((webchats) => webchats?.length > 0));
  private route = inject(ActivatedRoute);

  readonly businessAppSettingsPageUrl$ = this.accountGroupId$.pipe(
    map((accountGroupId) => `/account/location/${accountGroupId}/settings`),
  );

  constructor(
    private readonly inboxApiService: InboxApiService,
    private readonly snackbarService: SnackbarService,
    private readonly inboxService: InboxService,
    private readonly router: Router,
    @Inject(ACCOUNT_GROUP_ID_TOKEN) readonly accountGroupId$: Observable<string>,
    @Inject(PARTNER_ID_TOKEN) protected readonly partnerId$: Observable<string>,
    @Inject(CONVERSATION_GOOGLE_BUSINESS_MESSAGES_AVAILABLE_TOKEN)
    readonly googleBusinessMessagesAvailable$: Observable<boolean>,
    @Inject(CONVERSATION_FACEBOOK_MESSENGER_AVAILABLE_TOKEN) readonly facebookMessengerAvailable$: Observable<boolean>,
    @Inject(CONVERSATION_INSTAGRAM_MESSAGES_AVAILABLE_TOKEN) readonly instagramMessengerAvailable$: Observable<boolean>,
    private readonly destroyRef: DestroyRef,
    private readonly conversationApiService: ConversationApiService,
  ) {
    this.displayGoogleBusinessCommunicationCard$ = this.googleBusinessMessagesAvailable$;
    this.facebookMessengerAvailable$;
    this.displayEmptyState$ = combineLatest([
      this.displayA2pCard$,
      this.displayGoogleBusinessCommunicationCard$,
      this.canAccessWebChatSettingsCard$,
      this.canAccessMessageTemplateSettingsCard$,
      this.facebookMessengerAvailable$,
    ]).pipe(
      map(
        ([
          displayA2pCard,
          displayGoogleCard,
          canAccessWebChatSettings,
          canAccessTemplates,
          facebookAvailable,
        ]): boolean => {
          const flags = [
            displayA2pCard,
            displayGoogleCard,
            canAccessWebChatSettings,
            canAccessTemplates,
            this.isPartnerCenter,
            facebookAvailable,
          ];
          return !flags.some(Boolean);
        },
      ),
    );
  }

  ngOnInit(): void {
    const showAvailabilityMessage = this.form.get('showAvailabilityMessage');
    if (showAvailabilityMessage) {
      showAvailabilityMessage.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((enabled) => {
        const field = this.form.get('availabilityMessage');
        enabled ? field?.enable() : field?.disable();
      });
    }

    this.partnerId$
      .pipe(
        tap(() => this.loading$$.next(true)),
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        switchMap((partnerId) =>
          this.inboxApiService.getConfiguration({
            subjectParticipant: {
              participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
              internalParticipantId: partnerId,
            },
          }),
        ),
      )
      .subscribe({
        next: (config) => {
          this.loading$$.next(false);
          this.updateForm(config.configuration);
        },
        error: (err) => {
          if (err.status !== 404) {
            this.snackbarService.openErrorSnack('INBOX.SETTINGS.ERROR_LOADING_SETTINGS');
          }
          this.loading$$.next(false);
        },
      });
  }

  protected async onSubmit(): Promise<void> {
    this.loading$$.next(true);

    const partnerId = await firstValueFrom(this.partnerId$);
    const req = this.inboxApiService.upsertConfiguration({
      configuration: {
        subjectParticipant: {
          participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
          internalParticipantId: partnerId,
        },
        showAvailabilityMessage: this.form.getRawValue().showAvailabilityMessage ?? undefined,
        availabilityMessage: this.form.getRawValue().availabilityMessage ?? undefined,
      },
    });

    await firstValueFrom(req)
      .then((resp) => {
        this.snackbarService.openSuccessSnack('INBOX.SETTINGS.SAVE_SUCCESSFUL');
        this.updateForm(resp.configuration);
      })
      .catch(() => {
        this.snackbarService.openErrorSnack('INBOX.SETTINGS.ERROR_SAVING_SETTINGS');
      });

    this.loading$$.next(false);
  }

  protected goToAutomations(): void {
    this.router
      .navigate(['/automations'])
      .then(() => this.router.navigate([{ outlets: { inbox: null } }], { queryParamsHandling: 'merge' }));
  }

  protected goToTemplatePage(): void {
    if (this.isBusinessApp) {
      this.router.navigate([`../message-templates`], {
        relativeTo: this.route,
      });
    } else {
      this.router.navigate(['', { outlets: { inbox: 'inbox/settings/message-templates' } }], {
        queryParamsHandling: 'merge',
      });
    }
  }

  private updateForm(config: Configuration): void {
    this.form.patchValue({
      showAvailabilityMessage: config.showAvailabilityMessage,
      availabilityMessage: config.availabilityMessage,
    });
  }
}
